<template>
  <div v-if="workOrder">
    <header id="page-header">
      <div class="content content-header pt-3">
        <h3 class="my-0">
          <b-button
            class="pl-0 pr-2 font-w700 font-size-lg d-md-none"
            variant="link"
            @click="$store.commit('setSidebarCollapsed', !$store.state.layout.sidebarCollapsed)"
          >
            <i class="fa fa-bars" />
          </b-button>
        </h3>
        <h3 class="my-0">
          <b-button
            @click="() => $router.go(-1)"
            class="pl-0 pr-2 font-w700 font-size-lg d-none d-md-block"
            variant="link"
          >
            <i class="fa fa-arrow-left" />
          </b-button>
        </h3>

        <div class="d-lg-flex flex-grow-1 align-items-center gap-2 overflow-hidden">
          <h1 class="h3 my-0 text-dark font-w600">
            {{ workOrder.number }}
          </h1>

          <status-badge
            :status="workOrder.status"
            :sub-status="workOrder.sub_status"
            :status-variants="statusVariants"
          />
        </div>

        <div class="d-flex flex-wrap align-items-center justify-content-end gap-2 flex-shrink-0">
          <b-button
            v-if="!showCustomFieldsForm && $can('update', 'WorkOrder')"
            variant="primary"
            v-b-modal.work-order-edit-modal
            class="d-none d-md-inline-block"
            v-b-tooltip.hover
            title="Edit the Work Order"
          >
            <i class="fa fa-pencil-alt mr-1" /> Edit
          </b-button>

          <b-dropdown
            v-if="showCustomFieldsForm && $can('update', 'WorkOrder')"
            variant="primary"
            class="d-none d-md-inline-block"
            split
            @click="$bvModal.show('work-order-edit-modal')"
            no-flip
            right
            v-b-tooltip.hover
            title="Edit the Work Order"
          >
            <template #button-content>
              <i class="fa fa-pencil-alt mr-1" /> Edit
            </template>
            <b-dropdown-item
              v-b-modal.custom-field-values-form-modal
            >
              <i class="fa fa-pencil-alt mr-1" />
              Edit Additional Info
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown
            v-if="$can('update', 'WorkOrder') && workOrder.officetrax_reference_id"
            variant="outline-secondary"
            v-b-tooltip.hover
            title="Manage Officetrax data"
          >
            <template #button-content>
              <span>Officetrax</span>
              <span class="font-size-xs bg-gray p-1 d-none d-md-inline-block">
                3rd Party
              </span>
            </template>
            <b-dropdown-item
              v-b-modal.work-order-officetrax-modal
            >
              <i class="fa fa-pencil-alt mr-1" />
              Edit Data
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('upload_photo', 'WorkOrder')"
              @click="uploadOfficetraxPhotos"
            >
              <i class="fa fa-upload mr-1" />
              Upload Photos
            </b-dropdown-item>
          </b-dropdown>

          <fexa-assignment-manager
            v-if="$can('update', 'WorkOrder') && isFexaWorkOrder"
            :value="workOrder"
            @input="onWorkOrderChanged"
            @status-changed="(status) => fexaAssignmentStatus = status"
          />

          <b-button
            v-if="$can('assign', workOrder) && isUnassignedOrPending"
            variant="outline-secondary"
            v-b-modal.work-order-assign-modal
            class="d-none d-md-inline-block"
            v-b-tooltip.hover
            title="Assign the Work Order"
          >
            <i class="fa fa-user-plus mr-1" /> Assign
          </b-button>

          <b-button
            v-if="$can('complete', workOrder) && !isUnassignedOrPending"
            :key="`${workOrderKey}-complete-button`"
            variant="outline-secondary"
            v-b-modal.work-order-complete-modal
            class="d-none d-md-inline-block"
            v-b-tooltip.hover
            title="Complete the Work Order"
          >
            <i class="fa fa-calendar-plus mr-1" /> Complete
          </b-button>

          <b-button
            v-if="canApproveWorkOrder"
            variant="outline-secondary"
            class="d-none d-md-inline-block"
            v-b-tooltip.hover
            title="Approve the Work Order"
            @click="approveWorkOrder"
          >
            <i class="fa fa-check mr-1" /> Approve
          </b-button>

          <sfs-download-button
            :filename="`${workOrder.number}.pdf`"
            :provider-params="{ id: workOrder.id }"
            :file-provider="$workOrdersAPI.getPDF"
            v-b-tooltip.hover
            title="Download the Work Order"
          />

          <b-dropdown
            variant="outline-secondary"
            no-caret
            right
          >
            <template #button-content>
              <i class="fa fa-ellipsis-v" />
            </template>

            <div class="d-md-none">
              <b-dropdown-item
                v-if="!showCustomFieldsForm && $can('update', 'WorkOrder')"
                v-b-modal.work-order-edit-modal
              >
                <i class="fa fa-pencil-alt mr-1" /> Edit
              </b-dropdown-item>

              <b-dropdown-item
                v-if="showCustomFieldsForm && $can('update', 'WorkOrder')"
                v-b-modal.custom-field-values-form-modal
              >
                <i class="fa fa-pencil-alt mr-1" /> Edit Additional Info
              </b-dropdown-item>

              <b-dropdown-item
                v-if="$can('assign', workOrder) && isUnassignedOrPending"
                v-b-modal.work-order-assign-modal
              >
                <i class="fa fa-user-plus mr-1" /> Assign
              </b-dropdown-item>

              <b-dropdown-item
                v-if="$can('complete', workOrder) && !isUnassignedOrPending"
                :key="`${workOrderKey}-complete-button`"
                v-b-modal.work-order-complete-modal
              >
                <i class="fa fa-calendar-plus mr-1" /> Complete
              </b-dropdown-item>

              <b-dropdown-item
                v-if="canApproveWorkOrder"
                @click="approveWorkOrder"
              >
                <i class="fa fa-check mr-1" /> Approve
              </b-dropdown-item>

              <b-dropdown-divider />

              <div v-if="$can('update', 'WorkOrder') && workOrder.officetrax_reference_id">
                <b-dropdown-item
                  v-b-modal.work-order-officetrax-modal
                >
                  <i class="fa fa-pencil-alt mr-1" />
                  Edit Officetrax Data
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="$can('upload_photo', 'WorkOrder')"
                  @click="uploadOfficetraxPhotos"
                >
                  <i class="fa fa-upload mr-1" />
                  Upload Officetrax Photos
                </b-dropdown-item>

                <b-dropdown-divider />
              </div>
            </div>

            <b-dropdown-item
              v-if="$can('assign', workOrder) && !isUnassignedOrPending"
              v-b-modal.work-order-assign-modal
              :key="`${workOrderKey}-assign-dropdown`"
            >
              <i class="fa fa-user-edit mr-1" />
              Assign
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('reassign', workOrder)"
              v-b-modal.work-order-reassign-modal
              :key="`${workOrderKey}-reassign-dropdown`"
            >
              <i class="fa fa-user-edit mr-1" />
              Reassign
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('complete', workOrder) && isUnassignedOrPending"
              v-b-modal.work-order-complete-modal
              :key="`${workOrderKey}-complete-dropdown`"
            >
              <i class="fa fa-calendar-plus mr-1" />
              Complete
            </b-dropdown-item>

            <b-dropdown-item
              v-if="canUndoCompletion"
              :key="`${workOrderKey}-undo-check-out-dropdown`"
              @click="undoCompletion"
            >
              <i class="fa fa-undo mr-1" />
              Undo Check-In / Out
            </b-dropdown-item>

            <b-dropdown-item
              v-if="canCancelWorkOrder"
              :key="`${workOrderKey}-cancel-dropdown`"
              v-b-modal.work-order-cancel-modal
            >
              <i class="fa fa-trash mr-1" />
              Cancel
            </b-dropdown-item>

            <b-dropdown-item v-b-modal.work-order-email-modal>
              <i class="fa fa-envelope mr-1" />
              Email
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('uncancel', workOrder)"
              :key="`${workOrderKey}-uncancel-dropdown`"
              @click="reopen"
            >
              <i class="fa fa-trash mr-1" />
              Reopen
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('create', 'ClientFeedback')"
              @click="() => $router.push({ name: 'new-client-feedback', query: { work_order_id: workOrder.id } })"
            >
              <i class="far fa-comment-dots mr-1" />
              Gather Feedback
            </b-dropdown-item>

            <b-dropdown-divider />

            <b-dropdown-item
              v-if="$can('update', 'WorkOrder') && !parent"
              :key="`${workOrderKey}-link-dropdown`"
              v-b-modal.work-order-link-modal
            >
              <i class="fa fa-link mr-1" />
              Link to Work Order
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('update', 'WorkOrder') && parent"
              :key="`${workOrderKey}-unlink-dropdown`"
              @click="unlinkParent"
            >
              <i class="fa fa-link mr-1" />
              Unlink Work Order
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('create', 'WorkOrder')"
              @click="() => $router.push({ name: 'new-work-order', query: { duplicate_id: workOrder.id } })"
            >
              <i class="fa fa-clone mr-1" />
              Duplicate
            </b-dropdown-item>

            <b-dropdown-divider />

            <b-dropdown-item
              :to="{ name: 'work-orders', query: { parent_id: workOrder.id.toString() } }"
            >
              <i class="fas fa-external-link-alt mr-1" />
              View linked WOs
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('read', 'Version')"
              :to="{ name: 'versions', query: { model_id: workOrder.id, model: 'WorkOrder' } }"
            >
              <i class="fa fa-glasses mr-1" />
              Audit Log
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <div class="content pt-0 pb-3">
        <div class="d-flex flex-wrap align-items-center text-muted font-size-sm">
          <span class="d-inline-flex align-items-center">
            <i class="fa fa-map-marker-alt mr-2 text-primary" />
            <b-link
              :to="{ name: 'location', params: { id: workOrder.location_id } }"
              class="text-secondary"
            >
              Store #{{ workOrder.location.store_number }}
              <span v-if="locationCityState"> &middot; {{ locationCityState }}</span>
            </b-link>
          </span>
          <template v-if="workOrder.service">
            <span class="text-muted mx-2">|</span>
            <span class="d-inline-flex align-items-center">
              <i class="fa fa-wrench mr-2 text-primary" />
              <b-link
                :to="{ name: 'service', params: { id: workOrder.service_id } }"
                class="text-secondary"
              >
                {{ workOrder.service.name }}
              </b-link>
            </span>
          </template>
          <span class="text-muted mx-2">|</span>
          <span class="d-inline-flex align-items-center">
            <i class="fa fa-building mr-2 text-primary" />
            <b-link
              :to="{ name: 'client', params: { id: workOrder.client.id } }"
              class="text-secondary"
            >
              {{ workOrder.client.name }}
            </b-link>
          </span>
        </div>
      </div>

      <div class="content border-top border-gray py-3">
        <b-row>
          <b-col
            cols="6"
            md
            class="mb-3 mb-md-0"
          >
            <div class="text-muted font-w500 font-size-sm mb-1">
              Assigned To
            </div>
            <div v-if="workOrder.assigned_to">
              <b-link :to="{ name: 'user', params: { id: workOrder.assigned_to.id } }">
                {{ workOrder.assigned_to.name }}
              </b-link>
            </div>
            <div
              v-else
              class="text-muted"
            >
              -
            </div>
          </b-col>

          <b-col
            v-if="!isCompleted"
            cols="6"
            md
            class="mb-3 mb-md-0"
          >
            <div class="text-muted font-w500 font-size-sm mb-1">
              Date Bucket
            </div>
            <div
              v-if="workOrder.date_bucket_start"
              class="font-w600"
            >
              {{ workOrder | dateRangeFormat("date_bucket_start", "date_bucket_end", workOrder.location.time_zone) }}
            </div>
            <div
              v-else
              class="text-muted"
            >
              -
            </div>
          </b-col>

          <b-col
            v-if="isCompleted"
            cols="6"
            md
            class="mb-3 mb-md-0"
          >
            <div class="text-muted font-w500 font-size-sm mb-1">
              Work Completed
            </div>
            <div class="font-w600">
              <div>
                {{ workOrder | dateRangeFormat("checked_in_at", "checked_out_at", workOrder.location.time_zone) }}
              </div>
              <div class="font-size-sm font-w400">
                {{ workOrder | timeRangeFormat("checked_in_at", "checked_out_at", workOrder.location.time_zone) }}
              </div>
            </div>
          </b-col>

          <b-col
            cols="6"
            md
            class="mb-3 mb-md-0"
          >
            <div class="text-muted font-w500 font-size-sm mb-1">
              Priority
            </div>
            <div
              v-if="workOrder.priority"
              class="font-w600"
            >
              {{ workOrder.priority }}
            </div>
            <div
              v-else
              class="text-muted"
            >
              -
            </div>
          </b-col>

          <b-col
            cols="6"
            md
            class="mb-3 mb-md-0"
          >
            <div class="text-muted font-w500 font-size-sm mb-1">
              Cost / Price
            </div>
            <div class="font-w600 d-flex">
              <money-format
                v-if="costDisplay"
                :value="costDisplay.amount"
                :currency-code="costDisplay.currency"
                subunits-value
              />
              <span v-else>-</span>
              /
              <money-format
                v-if="priceDisplay"
                :value="priceDisplay.amount"
                :currency-code="priceDisplay.currency"
                subunits-value
              />
              <span v-else>-</span>
            </div>
          </b-col>

          <b-col
            cols="6"
            md
          >
            <div class="text-muted font-w500 font-size-sm mb-1">
              Parent Work Order
            </div>
            <b-link
              v-if="parent"
              :to="{ name: 'work-order', params: { id: parent.id } }"
            >
              {{ parent.number }}
            </b-link>
            <div
              v-else
              class="text-muted"
            >
              -
            </div>
          </b-col>
        </b-row>
      </div>
    </header>

    <div class="content">
      <b-tabs
        content-class="pt-4 mb-3"
        v-model="activeTab"
        nav-class="wo-show-tabs"
      >
        <template #tabs-end>
          <div
            v-show="activeTab === 2"
            class="ml-auto"
          >
            <b-button
              v-if="$can('create', 'WorkOrderCharge')"
              v-b-modal.work-order-charge-add-modal
              variant="outline-secondary"
              size="sm"
            >
              <i class="fa fa-plus mr-1" /> Add Charge
            </b-button>
            <sfs-download-button
              v-b-tooltip.hover
              class="ml-2"
              filename="Work Order Charges.csv"
              :file-provider="getChargesSpreadsheet"
              title="Download spreadsheet"
              size="sm"
            />
          </div>

          <div
            v-show="activeTab === 3"
            class="ml-auto"
          >
            <b-button
              size="sm"
              variant="outline-secondary"
              @click="$refs.notesTimeline && $refs.notesTimeline.toggleTimelineForm()"
            >
              <i class="fa fa-plus mr-1" /> New Note
            </b-button>
          </div>

          <div
            v-show="activeTab === 4"
            class="ml-auto"
          >
            <b-button
              size="sm"
              variant="outline-secondary"
              v-b-modal.work-order-add-photos-modal
            >
              <i class="fa fa-plus mr-1" /> Add Photos
            </b-button>
          </div>

          <div
            v-show="activeTab === 5"
            class="ml-auto"
          >
            <b-button
              v-if="$can('create', 'Document')"
              v-b-modal.new-document-modal
              variant="outline-secondary"
              size="sm"
            >
              <i class="fa fa-plus mr-1" /> Add Document
            </b-button>
          </div>

          <div
            v-show="activeTab === 6"
            class="ml-auto"
          >
            <b-button
              v-if="$can('create', 'Quote')"
              @click="() => $router.push({ name: 'new-quote', query: { work_order_id: workOrder.id } })"
              variant="outline-secondary"
              size="sm"
            >
              <i class="fa fa-plus mr-1" /> New Quote
            </b-button>
          </div>
        </template>

        <b-tab
          active
          title="Details"
        >
          <b-row>
            <b-col
              cols="12"
              lg="8"
            >
              <div
                v-if="workOrder.description || headerChips.length"
                class="mb-4"
              >
                <template v-if="workOrder.description">
                  <div class="text-muted font-w500 font-size-sm mb-1">
                    Description
                  </div>
                  <div class="text-prewrap">
                    {{ workOrder.description }}
                  </div>
                </template>
                <div
                  v-if="headerChips.length"
                  class="d-flex flex-wrap gap-2"
                  :class="{ 'mt-2': workOrder.description }"
                >
                  <b-badge
                    v-for="chip in headerChips"
                    :key="chip"
                    variant="primary"
                    class="font-size-sm font-w500"
                  >
                    {{ chip }}
                  </b-badge>
                </div>
              </div>

              <b-row>
                <b-col
                  cols="12"
                  sm="6"
                  class="mb-3"
                >
                  <div class="text-muted font-w500 font-size-sm mb-1">
                    Customer Identifier
                  </div>
                  <div>{{ workOrder.customer_identifier || '-' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  sm="6"
                  class="mb-3"
                  v-if="workOrder.completion_source"
                >
                  <div class="text-muted font-w500 font-size-sm mb-1">
                    Completion Source
                  </div>
                  <div>
                    {{ workOrder.completion_source | humanizedFormat | capitalizeFormat }}
                  </div>
                </b-col>

                <b-col
                  cols="12"
                  sm="6"
                  class="mb-3"
                  v-if="workOrder.cancellation_reason"
                >
                  <div class="text-muted font-w500 font-size-sm mb-1">
                    Cancellation Reason
                  </div>
                  <div>
                    {{ workOrder.cancellation_reason | humanizedFormat | capitalizeFormat }}
                  </div>
                </b-col>

                <b-col
                  cols="12"
                  sm="6"
                  class="mb-3"
                >
                  <div class="text-muted font-w500 font-size-sm mb-1">
                    Maintenance Type
                  </div>
                  <div>{{ workOrder.maintenance_type || '-' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  sm="6"
                  class="mb-3"
                >
                  <div class="text-muted font-w500 font-size-sm mb-1">
                    Capital Expenditure
                  </div>
                  <div>{{ workOrder.capex ? 'Yes' : 'No' }}</div>
                </b-col>

                <b-col
                  v-for="(customData, index) in customFieldData"
                  :key="index"
                  cols="12"
                  sm="6"
                  class="mb-3"
                >
                  <div class="text-muted font-w500 font-size-sm mb-1 text-prewrap">
                    {{ customData.custom_field?.label }}
                  </div>
                  <display-value-field
                    :custom-field="customData.custom_field"
                    :value="customData.value"
                  />
                </b-col>
              </b-row>

              <div class="mt-2">
                <hr>
                <h5 class="font-w500 mb-3">
                  Technicians
                </h5>
                <b-row>
                  <b-col
                    cols="12"
                    sm="6"
                    class="mb-3"
                  >
                    <div class="text-muted font-w500 font-size-sm mb-1">
                      Assigned To
                    </div>
                    <div v-if="workOrder.assigned_to">
                      <b-link :to="{ name: 'user', params: { id: workOrder.assigned_to.id } }">
                        {{ workOrder.assigned_to.name }}
                      </b-link>
                    </div>
                    <div
                      v-else
                      class="text-muted"
                    >
                      -
                    </div>
                  </b-col>
                  <b-col
                    cols="12"
                    sm="6"
                    class="mb-3"
                  >
                    <div class="text-muted font-w500 font-size-sm mb-1">
                      Completed By
                    </div>
                    <div v-if="doneByName">
                      <b-link :to="{ name: 'user', params: { id: workOrder.done_by.id } }">
                        {{ doneByName }}
                      </b-link>
                    </div>
                    <div
                      v-else
                      class="text-muted"
                    >
                      -
                    </div>
                  </b-col>
                </b-row>
              </div>

              <div
                v-if="hasIntegrationDetails"
                class="mt-2"
              >
                <hr>
                <h5 class="font-w500 mb-3">
                  Integration
                </h5>
                <b-row>
                  <b-col
                    v-if="isFexaWorkOrder"
                    cols="12"
                    sm="6"
                    class="mb-3"
                  >
                    <div class="text-muted font-w500 font-size-sm mb-1">
                      Fexa Assignment ID
                    </div>
                    <div v-if="workOrder.dollar_general_fexa_assignment_reference">
                      {{ workOrder.dollar_general_fexa_assignment_reference.source_id }}
                    </div>
                    <div
                      v-else
                      class="text-muted"
                    >
                      -
                    </div>
                  </b-col>

                  <b-col
                    v-if="isFexaWorkOrder"
                    cols="12"
                    sm="6"
                    class="mb-3"
                  >
                    <div class="text-muted font-w500 font-size-sm mb-1">
                      Visit ID
                    </div>
                    <div v-if="workOrder.dollar_general_fexa_visit_reference">
                      {{ workOrder.dollar_general_fexa_visit_reference.source_id }}
                    </div>
                    <div
                      v-else
                      class="text-muted"
                    >
                      -
                    </div>
                  </b-col>

                  <b-col
                    v-if="isFexaWorkOrder"
                    cols="12"
                    sm="6"
                    class="mb-3"
                  >
                    <div class="text-muted font-w500 font-size-sm mb-1">
                      Assignment Status
                    </div>
                    <b-badge
                      v-if="fexaAssignmentStatus"
                      variant="primary"
                      class="font-size-sm"
                    >
                      {{ fexaAssignmentStatus }}
                    </b-badge>
                    <span
                      v-else
                      class="text-muted"
                    >-</span>
                  </b-col>

                  <b-col
                    v-if="workOrder.officetrax_reference_id"
                    cols="12"
                    sm="6"
                    class="mb-3"
                  >
                    <div class="text-muted font-w500 font-size-sm mb-1">
                      Close Out #
                    </div>
                    <div v-if="workOrder.integration_metadata && workOrder.integration_metadata.close_out_number">
                      {{ workOrder.integration_metadata.close_out_number }}
                    </div>
                    <div
                      v-else
                      class="text-muted"
                    >
                      -
                    </div>
                  </b-col>

                  <b-col
                    v-if="scheduledDate"
                    cols="12"
                    sm="6"
                    class="mb-3"
                  >
                    <div class="text-muted font-w500 font-size-sm mb-1">
                      Scheduled Date
                    </div>
                    <div>{{ scheduledDate | dateTimeFormat(workOrder.location.time_zone) }}</div>
                  </b-col>
                </b-row>
              </div>
            </b-col>

            <b-col
              cols="12"
              class="d-lg-none"
            >
              <hr class="my-0">
            </b-col>

            <b-col
              cols="12"
              lg="4"
              class="pl-lg-4 border-left border-gray"
            >
              <!-- Location -->
              <div class="pb-4">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <h5 class="font-w500 my-0">
                    Location
                  </h5>
                  <b-link
                    v-if="mapUrl"
                    :href="mapUrl"
                    target="_blank"
                    rel="noopener"
                  >
                    Open Map
                  </b-link>
                </div>

                <div class="row">
                  <div class="col-6 text-muted font-w500 font-size-sm">
                    Address
                  </div>
                  <div class="col-6 text-right font-size-sm mb-3">
                    <div>{{ workOrder.location.street }}</div>
                    <div>
                      {{
                        {
                          city: workOrder.location.city,
                          state: workOrder.location.state,
                          zip_code: workOrder.location.zip_code,
                        } | locationFormat
                      }}
                    </div>
                  </div>
                </div>

                <map-with-markers
                  :height="200"
                  :marker-distance="workOrder.check_in_distance"
                  :markers="[
                    { coord: { lat: parseFloat(workOrder.location.latitude), lng: parseFloat(workOrder.location.longitude) }, label: 'Work Order Location' },
                    { coord: { lat: parseFloat(workOrder.latitude), lng: parseFloat(workOrder.longitude) }, label: 'Check in Location' }
                  ]"
                />
              </div>

              <!-- Billing Summary -->
              <div class="pt-2 border-top border-gray">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <h5 class="font-w500 my-0">
                    Billing Summary
                  </h5>
                  <b-link
                    href="#"
                    @click.prevent="activeTab = 2"
                  >
                    View Charges
                  </b-link>
                </div>

                <b-row class="mb-3">
                  <b-col cols="6">
                    <div class="text-muted font-w500 font-size-sm mb-1">
                      Total Price
                    </div>
                    <div>
                      <money-format
                        v-if="priceDisplay"
                        :value="priceDisplay.amount"
                        :currency-code="priceDisplay.currency"
                        subunits-value
                      />
                      <span v-else>-</span>
                    </div>
                  </b-col>
                  <b-col cols="6">
                    <div class="text-muted font-w500 font-size-sm mb-1">
                      Total Cost
                    </div>
                    <div>
                      <money-format
                        v-if="costDisplay"
                        :value="costDisplay.amount"
                        :currency-code="costDisplay.currency"
                        subunits-value
                      />
                      <span v-else>-</span>
                    </div>
                  </b-col>
                </b-row>

                <div class="d-flex justify-content-between py-2">
                  <span class="text-muted font-w500 font-size-sm">Price limit</span>
                  <money-format
                    v-if="workOrder.price_limit"
                    :value="workOrder.price_limit.amount"
                    :currency-code="workOrder.price_limit.currency"
                    subunits-value
                  />
                  <span v-else>-</span>
                </div>
                <div class="d-flex justify-content-between py-2">
                  <span class="text-muted font-w500 font-size-sm">Cost limit</span>
                  <money-format
                    v-if="workOrder.cost_limit"
                    :value="workOrder.cost_limit.amount"
                    :currency-code="workOrder.cost_limit.currency"
                    subunits-value
                  />
                  <span v-else>-</span>
                </div>
                <div class="d-flex justify-content-between py-2">
                  <span class="text-muted font-w500 font-size-sm">Invoices</span>
                  <span v-if="invoices.length > 0">
                    <b-link
                      v-for="(invoice, index) in invoices"
                      :key="index"
                      class="ml-1"
                      :to="{ name: 'invoice', params: { id: invoice.id } }"
                    >
                      {{ invoice.number }}
                    </b-link>
                  </span>
                  <span
                    v-else
                    class="text-muted"
                  >-</span>
                </div>
                <div class="d-flex justify-content-between py-2">
                  <span class="text-muted font-w500 font-size-sm">Payments</span>
                  <span v-if="technicianPayments.length > 0">
                    <b-link
                      v-for="(technicianPayment, index) in technicianPayments"
                      :key="index"
                      class="ml-1"
                      :to="{ name: 'technician-payment', params: { id: technicianPayment.id } }"
                    >
                      {{ technicianPayment.number }}
                    </b-link>
                  </span>
                  <span
                    v-else
                    class="text-muted"
                  >-</span>
                </div>
              </div>
            </b-col>
          </b-row>

          <div
            v-if="hasSignature"
            class="mt-2"
          >
            <hr>
            <h5 class="font-w500 mb-3">
              Manager Review
            </h5>
            <b-row>
              <b-col
                md="7"
                class="border-right"
              >
                <div class="pb-3 pt-0">
                  <b-row>
                    <b-col
                      cols="auto"
                      class="mr-auto text-muted font-w500 font-size-sm"
                    >
                      Manager Name
                    </b-col>
                    <b-col cols="auto">
                      {{ workOrder.manager_review.name }}
                    </b-col>
                  </b-row>
                </div>

                <div class="pb-3">
                  <b-row class="mb-2">
                    <b-col
                      cols="auto"
                      class="mr-auto text-muted font-w500 font-size-sm"
                    >
                      Manager Comment
                    </b-col>
                    <b-col cols="auto">
                      {{ workOrder.manager_review.comments }}
                    </b-col>
                  </b-row>
                </div>

                <b-row>
                  <b-col
                    cols="auto"
                    class="mr-auto text-muted font-w500 font-size-sm"
                  >
                    Rating
                  </b-col>

                  <b-col cols="auto">
                    <star-rating
                      :rating="workOrder.manager_review.rating"
                      :star-size="36"
                      :show-rating="false"
                      read-only
                      active-color="#084298"
                    />
                  </b-col>
                </b-row>
              </b-col>
              <b-col md="5">
                <b-row class="h-100">
                  <b-col
                    cols="auto"
                    class="mr-auto"
                  >
                    <img
                      v-if="signatureUrl"
                      class="img-fluid img-signature"
                      :src="signatureUrl"
                    >
                    <p v-else>
                      Signature not found.
                    </p>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </div>
        </b-tab>

        <b-tab title="Activity">
          <work-order-activities-timeline
            :work-order-id="workOrder.id"
            :refresh-key="activitiesRefreshKey"
          />
        </b-tab>

        <b-tab title="Charges">
          <work-order-charges-table
            :work-order-id="workOrder.id"
            :work-order="workOrder"
            :append-to-query="false"
            :show-per-page="false"
            :key="chargesKey"
            @total-rows-changed="(newTotal) => chargesTotal = newTotal"
          />
        </b-tab>

        <b-tab title="Notes">
          <notes-timeline
            ref="notesTimeline"
            :list-func="$notesAPI.getWorkOrdersNotes"
            :create-func="$notesAPI.createWorkOrdersNote"
            :reference-id="workOrder.id"
            reference-type="WorkOrder"
            hide-new-note-button
            @total-rows-changed="onNotesTotalChanged"
          />
        </b-tab>

        <b-tab>
          <template #title>
            <number-badge
              title="Photos"
              :total="photosTotal"
            />
          </template>
          <photos-panel
            :work-order="workOrder"
            @on-save="getWorkOrder"
            @on-destroy="getWorkOrder"
          />
        </b-tab>

        <b-tab>
          <template #title>
            <number-badge
              title="Documents"
              :total="documentsTotal"
            />
          </template>
          <documents-table
            :columns="$store.getters.columnsForTable(this.documentsTableName)"
            :append-to-query="false"
            :show-per-page="false"
            :key="documentsKey"
            :list-provider="$documentsAPI.getWorkOrderDocuments"
            :reference-id="workOrder.id"
            @total-rows-changed="(newTotal) => documentsTotal = newTotal"
          />
        </b-tab>

        <b-tab>
          <template #title>
            <number-badge
              title="Quotes"
              :total="quotesTotal"
            />
          </template>
          <quotes-table
            :append-to-query="false"
            :show-per-page="false"
            :filters="{ work_order_id: workOrder.id }"
            :columns="quotesColumns"
            :key="quotesKey"
            @data-changed="refreshCharges"
            @total-rows-changed="(newTotal) => quotesTotal = newTotal"
          />
        </b-tab>

        <b-tab title="History">
          <work-order-history-list-filter
            @filters-applied="applyHistoryFilters"
          />
          <work-order-history-table
            :location-id="workOrder.location_id"
            :client-id="workOrder.client_id"
            :filters="historyFilters"
            :append-to-query="false"
            :key="historyKey"
          />
        </b-tab>
      </b-tabs>
    </div>

    <!-- Modals -->
    <edit-modal
      v-if="$can('update', 'WorkOrder')"
      :key="`${workOrderKey}-edit-modal`"
      :value="workOrder"
      @input="onWorkOrderChanged"
    />
    <assign-modal
      v-if="workOrder && $can('assign', workOrder)"
      :key="`${workOrderKey}-assign-modal`"
      :value="workOrder"
      :can-assign-cost="chargesTotal == 1"
      @input="onWorkOrderChanged"
    />
    <reassign-modal
      v-if="workOrder && $can('reassign', workOrder)"
      :key="`${workOrderKey}-reassign-modal`"
      :value="workOrder"
      @input="onWorkOrderChanged"
    />
    <complete-modal
      v-if="workOrder && $can('complete', workOrder)"
      :key="`${workOrderKey}-complete-modal`"
      :value="workOrder"
      @input="onWorkOrderChanged"
    />
    <link-modal
      v-if="$can('update', 'WorkOrder') && !parent"
      :key="`${workOrderKey}-link-modal`"
      :value="workOrder"
      @input="(newParent) => parent = newParent"
    />
    <email-modal
      v-model="workOrder"
      :key="`${workOrderKey}-email-modal`"
      :email="workOrder.assigned_to ? workOrder.assigned_to.email : ''"
    />
    <edit-officetrax-modal
      v-if="$can('update', 'WorkOrder')"
      :key="`${workOrderKey}-edit-officetrax-modal`"
      :value="workOrder"
      @input="onWorkOrderChanged"
    />
    <custom-field-values-form-modal
      v-if="$can('update', 'WorkOrder')"
      :key="`${workOrderKey}-edit-custom-fields-modal`"
      :value="workOrder"
      :custom-field-values="customFieldData"
      @data-changed="(customFields) => showCustomFieldsForm = customFields.length > 0"
      @input="(values) => customFieldData = values"
    />
    <cancel-modal
      :work-order="workOrder"
      :key="`${workOrderKey}-cancel-modal`"
      @cancellation-reason-changed="onWorkOrderChanged"
    />
    <new-document-modal
      v-if="$can('update', 'WorkOrder')"
      :reference-id="workOrder.id"
      :create-provider="$documentsAPI.createWorkOrderDocument"
      :internal="$store.getters.isBMSOrganization"
      @on-save="refreshDocuments()"
      :key="documentsKey"
    />
    <new-charge-modal
      v-if="$can('create', 'WorkOrderCharge')"
      :work-order="workOrder"
      @on-save="refreshCharges"
    />
  </div>
</template>

<script>
import CancelModal from '@/components/admin/workOrders/CancelModal.vue';
import StarRating from 'vue-star-rating';
import WorkOrderChargesTable from '@/components/admin/workOrderCharges/Table.vue';
import WorkOrderHistoryListFilter from '@/components/admin/workOrders/HistoryListFilter.vue';
import WorkOrderHistoryTable from '@/components/admin/workOrders/HistoryTable.vue';
import EditModal from '@/components/admin/workOrders/EditModal.vue';
import LinkModal from '@/components/admin/workOrders/LinkModal.vue';
import AssignModal from '@/components/admin/workOrders/AssignModal.vue';
import ReassignModal from '@/components/admin/workOrders/ReassignModal.vue';
import CompleteModal from '@/components/admin/workOrders/CompleteModal.vue';
import MapWithMarkers from '@/components/shared/MapWithMarkers.vue';
import NewDocumentModal from '@/components/admin/documents/NewModal.vue';
import DocumentsTable from '@/components/admin/documents/Table.vue';
import QuotesTable from '@/components/admin/quotes/Table.vue';
import StatusBadge from '@/components/shared/StatusBadge.vue';
import PhotosPanel from '@/components/admin/workOrders/PhotosPanel.vue';
import EmailModal from '@/components/admin/workOrders/EmailModal.vue';
import NewChargeModal from '@/components/admin/workOrderCharges/NewModal.vue';
import EditOfficetraxModal from '@/components/admin/workOrders/EditOfficetraxModal.vue';
import FexaAssignmentManager from '@/components/admin/integrationReferences/Fexa/AssignmentManager.vue';
import CustomFieldValuesFormModal from '@/components/admin/customFields/CustomFieldValuesFormModal.vue';
import DisplayValueField from '@/components/admin/customFields/DisplayValueField.vue';
import NotesTimeline from '@/components/admin/notes/Timeline.vue';
import WorkOrderActivitiesTimeline from '@/components/admin/workOrderActivities/Timeline.vue';
import NumberBadge from '@/components/shared/NumberBadge.vue';
import { isNil as _isNil } from 'lodash';
import { STATUS_VARIANTS, WORK_ORDER_STATUS_PENDING, WORK_ORDER_STATUS_UNASSIGNED, WORK_ORDER_STATUS_COMPLETED, WORK_ORDER_STATUS_INVOICED } from '@/constants/workOrders';
import { DOCUMENTS_TABLE_NAME } from '@/constants/documents';
import { WORK_ORDER_QUOTE_TABLE_DEFAULT_COLUMNS } from '@/constants/quotes';
import errorToastMixin from '@/mixins/errorToastMixin';

export default {
  name: 'WorkOrdersShow',
  mixins: [errorToastMixin],
  components: {
    StarRating,
    CancelModal,
    WorkOrderChargesTable,
    WorkOrderHistoryListFilter,
    WorkOrderHistoryTable,
    EditModal,
    LinkModal,
    AssignModal,
    ReassignModal,
    StatusBadge,
    CompleteModal,
    NewDocumentModal,
    DocumentsTable,
    PhotosPanel,
    EmailModal,
    NewChargeModal,
    EditOfficetraxModal,
    CustomFieldValuesFormModal,
    DisplayValueField,
    FexaAssignmentManager,
    NotesTimeline,
    WorkOrderActivitiesTimeline,
    MapWithMarkers,
    QuotesTable,
    NumberBadge,
  },
  data() {
    return {
      documentsTableName: DOCUMENTS_TABLE_NAME,
      quotesColumns: WORK_ORDER_QUOTE_TABLE_DEFAULT_COLUMNS,
      workOrder: null,
      parent: null,
      statusVariants: STATUS_VARIANTS,
      invoices: [],
      technicianPayments: [],
      customFieldData: [],
      showCustomFieldsForm: false,
      fexaAssignmentStatus: null,
      chargesTotal: 0,
      documentsTotal: 0,
      quotesTotal: 0,
      activitiesRefreshKey: 0,
      notesLoaded: false,
      workOrderKey: `${Date.now()}`,
      documentsKey: `documents-${Date.now()}`,
      chargesKey: `charges-${Date.now()}`,
      quotesKey: `quotes-${Date.now()}`,
      historyKey: `history-${Date.now()}`,
      historyFilters: {},
      activeTab: 0,
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.parent = null;
      this.getWorkOrder();
      this.getTechnicianPayments();
      this.getCustomFieldData();
      if (this.$can('read', 'Invoice')) {
        this.getInvoices();
      }
    },
    getWorkOrder() {
      const isRefresh = !!this.workOrder;
      this.$workOrdersAPI
        .get(this.$route.params.id)
        .then((workOrder) => {
          this.workOrder = workOrder;
          if (isRefresh) {
            this.refreshActivities();
          }
          if (this.workOrder.parent_id) {
           this.$workOrdersAPI
            .get(this.workOrder.parent_id)
            .then((workOrder) => {
              this.parent = workOrder;
            });
          } else {
            this.parent = null;
          }
        })
        .catch(() => {
          this.$router.push('/404');
        });
    },
    getInvoices() {
      this.$invoicesAPI.getInvoices({ work_order_id: this.$route.params.id }).then((response) => {
        this.invoices = response.invoices;
      });
    },
    getTechnicianPayments() {
      this.$technicianPaymentsAPI.getTechnicianPayments({ work_order_id: this.$route.params.id }).then((response) => {
        this.technicianPayments = response.technicianPayments;
      });
    },
    getCustomFieldData() {
      this.$customFieldsAPI.getWorkOrderValues(this.$route.params.id).then(({ customFieldValues }) => {
        this.customFieldData = customFieldValues;
      });
    },
    unlinkParent() {
      this.$swal({
        text: `Are you sure you want to unlink this Work order from ${this.parent?.number}?`,
        preConfirm: () => this.$workOrdersAPI.update(this.workOrder.id, { parent_id: null }),
      }).then((result) => {
        if (result.value) {
          this.workOrder = result.value;
          this.parent = null;
        }
        result.timer;
      });
    },
    reopen() {
      this.$swal({
        text: 'Are you sure you want to reopen this Work Order?',
        preConfirm: () => this.$workOrdersAPI.uncancel(this.workOrder.id),
      }).then((result) => {
        if (result.value) {
          this.workOrder = result.value;
        }
        result.timer;
      });
    },
    undoCompletion() {
      this.$swal({
        text: 'Do you want to undo the Check-In / Out?',
        preConfirm: () => this.$workOrdersAPI.undoCompletion(this.workOrder.id).catch((error) => {
          if (this.$swal && typeof this.$swal.close === 'function') {
            this.$swal.close();
          }
          this.$nextTick(() => this.displayErrorToast(error, {
            fallbackMessage: 'We\'ve experienced an issue while trying to undo the work order completion, Please try again.',
          }));
          return false;
        }),
      }).then((result) => {
        if (result.value) {
          this.workOrder = result.value;
        }
        result.timer;
      });
    },
    approveWorkOrder() {
      this.$swal({
        text: 'Are you sure you want to approve this Work Order?',
        preConfirm: () => this.$workOrdersAPI.approve(this.workOrder.id).catch((error) => {
          if (this.$swal && typeof this.$swal.close === 'function') {
            this.$swal.close();
          }
          this.$nextTick(() => this.displayErrorToast(error, {
            fallbackMessage: 'We\'ve experienced an issue while trying to approve the work order, Please try again.',
          }));
          return false;
        }),
      }).then((result) => {
        if (result.value) {
          this.workOrder = result.value;
          this.workOrderKey = `${Date.now()}`;
        }
        result.timer;
      });
    },
    uploadOfficetraxPhotos() {
      this.$swal({
        text: 'Do you want to upload the Photos to Officetrax?',
        preConfirm: () => this.$workOrdersAPI.uploadOfficetraxPhotos(this.workOrder.id),
      }).then((result) => {
        if (result.value) {
          this.workOrder = result.value;
        }
        result.timer;
      });
    },
    applyHistoryFilters({ filters }) {
      this.historyFilters = filters;
    },
    refreshDocuments() {
      this.documentsKey = `documents-${Date.now()}`;
    },
    refreshCharges() {
      this.chargesKey = `charges-${Date.now()}`;
      this.refreshActivities();
    },
    getChargesSpreadsheet() {
      return this.$workOrderChargesAPI.getWorkOrderSpreadsheet(this.workOrder.id);
    },
    onWorkOrderChanged(workOrder) {
      this.workOrder = workOrder;
      this.workOrderKey = `${Date.now()}`;
      this.refreshCharges();
    },
    onNotesTotalChanged() {
      if (this.notesLoaded) {
        this.refreshActivities();
      }
      this.notesLoaded = true;
    },
    refreshActivities() {
      this.activitiesRefreshKey = Date.now();
    },
  },
  watch: {
    '$route': 'fetchData',
    activeTab(tab) {
      // Activity tab index: Details, Activity, Charges, Notes, Photos, Documents, Quotes, History
      if (tab === 1) {
        this.refreshActivities();
      }
    },
  },
  computed: {
    photosTotal() {
      if (!this.workOrder) { return 0; }
      return (this.workOrder.before_photos || []).length
        + (this.workOrder.after_photos || []).length
        + (this.workOrder.no_show_photos || []).length;
    },
    isFexaWorkOrder() {
      return this.workOrder.dollar_general_fexa_assignment_reference_id;
    },
    isUnassignedOrPending() {
      return [WORK_ORDER_STATUS_UNASSIGNED, WORK_ORDER_STATUS_PENDING].includes(this.workOrder.status);
    },
    isCompleted() {
      return [WORK_ORDER_STATUS_COMPLETED, WORK_ORDER_STATUS_INVOICED].includes(this.workOrder.status);
    },
    isCompletedApproved() {
      return this.workOrder.status === WORK_ORDER_STATUS_COMPLETED && !!this.workOrder.approved_at;
    },
    canUndoCompletion() {
      if (this.isCompletedApproved) {
        return false;
      }

      return this.$can('undo_completion', this.workOrder);
    },
    canCancelWorkOrder() {
      if (this.technicianPayments.length > 0) {
        return false;
      }

      if (this.$can('cancel', this.workOrder)) {
        return true;
      }

      return this.isCompletedApproved && this.$can('approve', 'WorkOrder');
    },
    hasSignature() {
      return !_isNil(this.workOrder.manager_review);
    },
    signatureUrl() {
      return this?.workOrder?.manager_review?.signature?.url;
    },
    requiresWorkOrderApproval() {
      return !!this.$store.getters.currentOrganization?.work_order_approval_required;
    },
    canApproveWorkOrder() {
      if (!this.requiresWorkOrderApproval || !this.workOrder) {
        return false;
      }

      return this.$can('approve', this.workOrder) && !this.workOrder.approved_at && this.isCompleted;
    },
    headerChips() {
      return this.workOrder.tags || [];
    },
    locationCityState() {
      return this.$options.filters.locationFormat({
        city: this.workOrder.location.city,
        state: this.workOrder.location.state,
      });
    },
    priceDisplay() {
      return this.workOrder.price || this.workOrder.price_limit;
    },
    costDisplay() {
      return this.workOrder.cost || this.workOrder.cost_limit;
    },
    doneByName() {
      if ((this.workOrder.done_by && this.workOrder.done_by_id != this.workOrder.assigned_to_id) || (this.workOrder.done_by && this.isCompleted)) {
        return this.workOrder.done_by.name;
      }
      return '';
    },
    scheduledDate() {
      return this.workOrder.integration_metadata && this.workOrder.integration_metadata.scheduled_date;
    },
    hasIntegrationDetails() {
      return this.isFexaWorkOrder || !!this.workOrder.officetrax_reference_id;
    },
    mapUrl() {
      const { latitude, longitude } = this.workOrder.location;
      if (!latitude || !longitude) {
        return null;
      }
      return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    },
  },
};
</script>
<style scoped>
.img-signature {
  max-height: 100px;
}
::v-deep .wo-show-tabs {
  border-bottom: 1px solid #e9ecef;
}
</style>
